import { Component, OnInit, ViewChild } from '@angular/core';
import { ColumnMode, DatatableComponent } from '@swimlane/ngx-datatable';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TreeComponent } from 'angular-tree-component';
import { BroadcastService, MsalService } from '@azure/msal-angular';
import { SP_SCOPES } from '../msal.constants';
import { LoginAwareComponent } from '../login-aware-component';
import { DiagnosticsServiceFactory } from '../services/diagnostics-service-factory';
import { QnAService } from '../services/qna-service';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

const SEARCH_QUERY_DEBOUNCE_TIMEOUT = 500;

@Component({
  selector: 'app-qna',
  templateUrl: './qna.component.html',
  styleUrls: ['./qna.component.scss']
})
export class QnAComponent extends LoginAwareComponent implements OnInit {
  public refinerItems: any[];
  public refinerOptions: any;
  public resultRows: Array<any>;
  public columnMode: ColumnMode;
  public refinerValue: string;
  public queryText: string;
  public modalContent: { question: string, answer: string };

  @ViewChild(DatatableComponent) resultsTable: DatatableComponent;
  @ViewChild(TreeComponent) refinerTree: TreeComponent;
  private searchTerms: Subject<string>;

  constructor(diagnosticsFactory: DiagnosticsServiceFactory,
    broadcastService: BroadcastService,
    authService: MsalService,
    private _service: QnAService,
    private _modalService: NgbModal) {
    super(diagnosticsFactory.create('QnAComponent'), broadcastService, authService)
    this.refinerOptions = {
      idField: 'value',
    };
    this.refinerItems = [];
    this.resultRows = [];
    this.columnMode = ColumnMode.force;
    this.queryText = null;
    this.refinerValue = null;

    // Setting debouncer for taking text from search textbox as the user types it in...
    this.searchTerms = new Subject<string>();
    this.searchTerms.pipe(
      debounceTime(SEARCH_QUERY_DEBOUNCE_TIMEOUT)).subscribe((text) => {
        this.queryText = text;
        this.refinerValue = null;
        this.refinerTree.treeModel.collapseAll();
        this._runQuery(this.queryText, this.refinerValue);
      });
  }

  ngOnInit() {
    super.ngOnInit();
    this._runQuery();
  }

  onResetFiltersClick() {
    this.refinerValue = null;
    this.refinerTree.treeModel.collapseAll();
    this.queryText = null;
    this._runQuery();
  }

  onRefinerChange(event) {
    if (this.refinerValue !== event.node.data.value) {
      this.refinerValue = event.node.data.value;
      this._runQuery(this.queryText, this.refinerValue).then(() => {
        this.refinerTree.treeModel.getNodeById(this.refinerValue).setActiveAndVisible();
      });
    }
  }

  onSearchTextChange(event) {
    const text = event.target.value;
    this.searchTerms.next(text);
  }

  onMoreDetailsClick(event, row, dialogTemplate) {
    // Workaround for https://github.com/swimlane/ngx-datatable/issues/721
    event.target.parentElement.parentElement.blur();

    let detailedAnswer = row.detailedAnswer;
    let link = row.documentLink;
    let answer = row.answer;

    if (!detailedAnswer || detailedAnswer.length == 0) {
      // The answer is an FAQ document.
      detailedAnswer = `<div><strong>Match found: </strong>${answer}</div><br><div><a href="${link}">Open the document to see more details</a></div>`;
    }

    this.modalContent = { question: row.question, answer: detailedAnswer };
    this._modalService.open(dialogTemplate, { size: 'lg', centered: true, backdropClass: 'white-backdrop' });
  }

  protected onLoginSuccess(token: any) {
    super.onLoginSuccess(token);
    this._runQuery();
  }

  private _runQuery(query: string = null, refiner: string = null) {
    if (!this.loggedIn) {
      return;
    }

    return this.authorize(SP_SCOPES).then(token => {
      return this._service.runQuery(this.constructor.name, query, refiner, token).toPromise()
        .then(result => {
          this.refinerItems = result.searchRefiners;
          this.resultRows = result.searchResults;
          this.resultsTable.offset = 0;
        })
        .catch(error => {
          this.diagnostics.logError(`Error loading data. Details: ${JSON.stringify(error)}`);
      });
    });
  }
}
